import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ShieldCheck, Truck, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();

  const subtotal = getCartTotal();
  const shipping = subtotal >= 50 ? 0 : 5.99;
  const total = subtotal + shipping;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="w-24 h-24 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="text-sky-500" size={48} />
            </div>
            <h1 
              className="text-3xl font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Your Cart is Empty
            </h1>
            <p 
              className="text-slate-600 mb-8"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              Looks like you haven't added any toys yet. Let's find something fun!
            </p>
            <Link to="/category/all">
              <Button 
                className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400]"
                data-testid="continue-shopping-btn"
              >
                Start Shopping
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <h1 
          className="text-3xl md:text-4xl font-bold text-slate-800 mb-8"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
        >
          Shopping Cart ({getCartCount()} items)
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div 
                key={item.id}
                className="bg-white rounded-2xl p-4 md:p-6 shadow-sm"
                data-testid={`cart-item-${item.id}`}
              >
                <div className="flex gap-4">
                  {/* Product Image */}
                  <Link to={`/product/${item.id}`} className="flex-shrink-0">
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-slate-100">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.id}`}>
                      <h3 
                        className="font-bold text-slate-800 mb-1 hover:text-sky-500 transition-colors line-clamp-2"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      >
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-slate-500 mb-2">Ages: {item.ageGroup} years</p>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Quantity */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus size={14} />
                        </Button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus size={14} />
                        </Button>
                      </div>

                      {/* Price & Remove */}
                      <div className="flex items-center justify-between sm:justify-end gap-4">
                        <p 
                          className="text-lg font-bold text-slate-800"
                          style={{ fontFamily: 'Quicksand, sans-serif' }}
                        >
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-500"
                          onClick={() => removeFromCart(item.id)}
                          data-testid={`cart-remove-${item.id}`}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Continue Shopping */}
            <Link to="/category/all" className="inline-block">
              <Button 
                variant="outline" 
                className="rounded-full"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Button>
            </Link>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 
                className="text-xl font-bold text-slate-800 mb-6"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-sm text-sky-500">
                    Add ${(50 - subtotal).toFixed(2)} more for FREE shipping!
                  </p>
                )}
                <div className="border-t pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-slate-800">Total</span>
                    <span 
                      className="text-lg font-bold text-slate-800"
                      data-testid="cart-total"
                    >
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full rounded-full py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400] hover:shadow-[0px_2px_0px_0px_#cc8400] active:shadow-none transition-all mb-4"
                onClick={() => navigate('/checkout')}
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2" size={20} />
              </Button>

              {/* Trust Badges */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <ShieldCheck className="text-green-500" size={18} />
                  <span>100% Secure Checkout</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Truck className="text-sky-500" size={18} />
                  <span>Free Shipping Over $50</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <RotateCcw className="text-orange-500" size={18} />
                  <span>30-Day Easy Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
